import { useState } from 'react'
import { Alert, Button, Dropdown, Form, Input, Modal, Select } from 'antd'
import { MoreOutlined, PlusOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ColumnsType } from 'antd/es/table'
import { DataTable } from '@kolab/dms-base/components/ui'
import { PageLayout } from '@kolab/dms-base/components/layout'
import { toast, notify, getFriendlyErrorMessage, optionalPhoneValidator } from '@kolab/dms-base/lib'
import {
  createReseller,
  listResellers,
  setResellerStatus,
  softDeleteReseller,
  updateReseller,
} from '../api/reseller.api'
import { pickDistributors } from '../api/lookups.api'
import { resellerKeys } from '../api/queryKeys'
import type { Reseller, ResellerStatus } from '../api/types'
import { StatusBadge } from '../components/common/StatusBadge'
import { Can, P, usePermissionStore } from '../rbac'
import { useCursorList } from '../hooks/useCursorList'
import { CursorListStateView } from '../components/common/QueryStateView'


type Mode = { kind: 'closed' } | { kind: 'create' } | { kind: 'edit'; reseller: Reseller }

export default function ResellersPage() {
  const [distributorFilter, setDistributorFilter] = useState<string | undefined>()
  const queryClient = useQueryClient()

  const { items, isLoading, isError, error, refetch, isFetchingNextPage, hasNextPage, fetchNextPage } = useCursorList<Reseller>({
    queryKey: resellerKeys.list({ pageSize: 20, distributorId: distributorFilter }),
    queryFn: (params) => listResellers(params),
    params: { distributorId: distributorFilter },
    pageSize: 20,
  })

  const { hasPermission } = usePermissionStore()

  const distributorsQuery = useQuery({
    queryKey: ['lookups', 'distributors'],
    queryFn: () => pickDistributors(),
    enabled: hasPermission(P.tenants.distributor.pick),
  })

  const [modal, setModal] = useState<Mode>({ kind: 'closed' })
  const [formError, setFormError] = useState<string | null>(null)

  function handleFormError(error: unknown, fallback: string) {
    if (error && typeof error === 'object' && 'status' in error) {
      const status = (error as { status?: number }).status
      if (status === 401 || status === 403 || (status && status >= 500)) return
    }
    setFormError(getFriendlyErrorMessage(error, fallback))
  }

  function openModal(mode: Mode) {
    setFormError(null)
    setModal(mode)
  }

  const createMut = useMutation({
    mutationFn: createReseller,
    onSuccess: () => {
      toast.success('Reseller created')
      queryClient.invalidateQueries({ queryKey: resellerKeys.all })
      setModal({ kind: 'closed' })
    },
    onError: (error: unknown) => {
      handleFormError(error, 'Failed to create reseller')
    },
  })

  const updateMut = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => updateReseller(id, { name }),
    onSuccess: () => {
      toast.success('Reseller updated')
      queryClient.invalidateQueries({ queryKey: resellerKeys.all })
      setModal({ kind: 'closed' })
    },
    onError: (error: unknown) => {
      handleFormError(error, 'Failed to update reseller')
    },
  })

  const statusMut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: ResellerStatus }) =>
      setResellerStatus(id, { status }),
    onSuccess: () => {
      toast.success('Status updated')
      queryClient.invalidateQueries({ queryKey: resellerKeys.all })
    },
    onError: (error: unknown) => {
      if (error && typeof error === 'object' && 'status' in error) {
        const status = (error as { status?: number }).status
        if (status === 401 || status === 403 || (status && status >= 500)) return
      }
      toast.error(getFriendlyErrorMessage(error, 'Failed to update reseller status'))
    },
  })

  const deleteMut = useMutation({
    mutationFn: softDeleteReseller,
    onSuccess: () => {
      toast.success('Reseller deactivated')
      queryClient.invalidateQueries({ queryKey: resellerKeys.all })
    },
    onError: (error: unknown) => {
      if (error && typeof error === 'object' && 'status' in error) {
        const status = (error as { status?: number }).status
        if (status === 401 || status === 403 || (status && status >= 500)) return
      }
      notify.error({
        message: 'Deactivation failed',
        description: getFriendlyErrorMessage(error, 'Failed to deactivate reseller'),
      })
    },
  })

  const columns: ColumnsType<Reseller> = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    {
      title: 'Distributor',
      dataIndex: 'distributorName',
      key: 'distributorName',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (s: string) => <StatusBadge status={s} />,
    },
    { title: 'Created', dataIndex: 'createdAt', key: 'createdAt' },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_, record) => {
        const items = [
          hasPermission(P.tenants.reseller.update) && {
            key: 'edit',
            label: 'Edit',
            onClick: () => openModal({ kind: 'edit', reseller: record }),
          },
          hasPermission(P.tenants.reseller.suspend) && {
            key: 'activate',
            label: 'Set ACTIVE',
            onClick: () => statusMut.mutate({ id: record.id, status: 'ACTIVE' }),
          },
          hasPermission(P.tenants.reseller.suspend) && {
            key: 'suspend',
            label: 'Suspend',
            onClick: () => statusMut.mutate({ id: record.id, status: 'SUSPENDED' }),
          },
          hasPermission(P.tenants.reseller.delete) && {
            key: 'delete',
            label: 'Deactivate',
            danger: true,
            onClick: () => deleteMut.mutate(record.id),
          },
        ].filter(Boolean) as { key: string; label: string; danger?: boolean; onClick: () => void }[]

        if (items.length === 0) return null
        return (
          <Dropdown trigger={['click']} menu={{ items }} getPopupContainer={() => document.body}>
            <Button size="small" icon={<MoreOutlined />} />
          </Dropdown>
        )
      },
    },
  ]

  const distributorOptions = (distributorsQuery.data?.items ?? []).map((d) => ({
    label: d.label,
    value: d.id,
  }))

  return (
    <PageLayout
      actions={
        <Can permission={P.tenants.reseller.create}>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal({ kind: 'create' })}>
            New reseller
          </Button>
        </Can>
      }
      filters={
        <Select
          allowClear
          placeholder="Filter by distributor"
          style={{ minWidth: 220 }}
          options={distributorOptions}
          value={distributorFilter}
          onChange={(val) => setDistributorFilter(val)}
        />
      }
    >
      <CursorListStateView isLoading={isLoading} isError={isError} error={error} refetch={refetch}>
        <DataTable<Reseller>
          rowKey="id"
          columns={columns}
          dataSource={items}
          pagination={false}
          infiniteScroll={{ isFetchingNextPage, hasNextPage, fetchNextPage }}
        />
      </CursorListStateView>

      <ResellerFormModal
        mode={modal}
        distributorOptions={distributorOptions}
        onCancel={() => setModal({ kind: 'closed' })}
        onCreate={(values) => createMut.mutate(values)}
        onEdit={(name) => {
          if (modal.kind === 'edit') updateMut.mutate({ id: modal.reseller.id, name })
        }}
        submitting={createMut.isPending || updateMut.isPending}
        error={formError}
        onClearError={() => setFormError(null)}
      />
    </PageLayout>
  )
}

type CreateResellerFormValues = {
  distributorId: string
  name: string
  adminEmail: string
  adminFullName: string
  adminPhone?: string
}

function ResellerFormModal({
  mode,
  distributorOptions,
  onCancel,
  onCreate,
  onEdit,
  submitting,
  error,
  onClearError,
}: {
  mode: Mode
  distributorOptions: { label: string; value: string }[]
  onCancel: () => void
  onCreate: (values: CreateResellerFormValues) => void
  onEdit: (name: string) => void
  submitting: boolean
  error: string | null
  onClearError: () => void
}) {
  const [form] = Form.useForm<CreateResellerFormValues>()
  const isCreate = mode.kind === 'create'
  const initial =
    mode.kind === 'edit'
      ? { name: mode.reseller.name, distributorId: mode.reseller.distributorId }
      : { name: '', distributorId: '', adminEmail: '', adminFullName: '', adminPhone: '' }

  return (
    <Modal
      open={mode.kind !== 'closed'}
      title={isCreate ? 'New reseller' : 'Edit reseller'}
      onCancel={onCancel}
      onOk={() => form.submit()}
      okButtonProps={{ loading: submitting }}
      destroyOnClose
    >
      {error && (
        <Alert type="error" message={error} closable onClose={onClearError} style={{ marginBottom: 16 }} />
      )}
      <Form
        form={form}
        layout="vertical"
        preserve={false}
        initialValues={initial}
        onFinish={(values) => {
          if (isCreate) onCreate(values)
          else onEdit(values.name)
        }}
      >
        <Form.Item
          name="distributorId"
          label="Distributor"
          rules={[{ required: true, message: 'Distributor is required' }]}
        >
          <Select
            options={distributorOptions}
            disabled={mode.kind === 'edit'}
            placeholder="Select a distributor"
          />
        </Form.Item>
        <Form.Item
          name="name"
          label="Reseller name"
          rules={[{ required: true, message: 'Name is required' }, { max: 200, message: 'Name must not exceed 200 characters' }]}
        >
          <Input placeholder="e.g. EAST-ACME" />
        </Form.Item>

        {isCreate && (
          <>
            <div style={{ margin: '8px 0 12px', color: '#6b7280', fontSize: 12 }}>
              The primary reseller admin is created in IAM asynchronously, in
              the same tenant as the parent distributor. A temporary password
              will be emailed; the user is forced to change it on first login.
            </div>
            <Form.Item
              name="adminFullName"
              label="Admin full name"
              rules={[{ required: true, message: 'Admin full name is required' }, { max: 200, message: 'Name must not exceed 200 characters' }]}
            >
              <Input placeholder="e.g. Ops East" />
            </Form.Item>
            <Form.Item
              name="adminEmail"
              label="Admin email"
              rules={[
                { required: true, message: 'Admin email is required' },
                { type: 'email', message: 'Enter a valid email address' },
              ]}
            >
              <Input placeholder="ops@example.com" />
            </Form.Item>
            <Form.Item
              name="adminPhone"
              label="Admin phone (optional)"
              rules={[{ validator: optionalPhoneValidator }]}
            >
              <Input placeholder="+1 555 0100" />
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  )
}

export { ResellersPage }
